/**
 * Duelo por Tiendas — WebSocket LIVE battle (2 teams · turn / strikes / steal).
 *
 * Flow:
 *   /juego/canales-dijeron/duelo         → Create room (host) or Join by code.
 *   /juego/canales-dijeron/duelo/CODE    → Enter room:
 *      1) Choose team A or B (or auto-host if you created the room).
 *      2) Host tweaks config (mode auto/host, rounds, strikes, steal).
 *      3) Host taps Start once each team has ≥1 player.
 *      4) Active team guesses; wrong / decoy = strike; N strikes = steal.
 *      5) Best-of-N rounds; alternating start team; winner has highest total.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Trophy, Clock, Play, Swords, Loader2, ArrowLeft, Zap, Copy, Users, Home,
  Volume2, VolumeX, Send, ChevronRight, X as XIcon, ShieldAlert, Crown,
  CheckCircle2, XCircle, EyeOff, Repeat, StopCircle, Settings, ChevronsRight,
  MessageCircle,
} from "lucide-react";

import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import { Input } from "@/components/ui/input";
import {
  playMiss, playApplause, playStart, playCountdown, playSteal,
  playReveal, playStealWin, playStealFail, startSuspense, stopSuspense,
  isEnabled as soundEnabled, toggleEnabled as toggleSound,
} from "@/utils/sounds";

const buildWsUrl = (code) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const proto = base.startsWith("https") ? "wss" : "ws";
  const host = base.replace(/^https?:\/\//, "");
  return `${proto}://${host}/api/dijeron/battle/ws/${code}`;
};

const stableClientId = () => {
  // Reuse the same anon id across reloads so host status survives refresh.
  const key = "canales_duelo_uid";
  let v = localStorage.getItem(key);
  if (!v) {
    v = `cli-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, v);
  }
  return v;
};

function Header({ snd, setSnd }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <Link to="/juego/canales-dijeron" className="brutal-btn bg-white px-3 py-2 text-sm inline-flex items-center gap-1" data-testid="duelo-back">
        <ArrowLeft className="w-4 h-4" /> Volver
      </Link>
      <CanalesLogo width={110} />
      <button
        onClick={() => setSnd(toggleSound())}
        data-testid="duelo-sound-toggle"
        className={`brutal-btn px-3 py-2 text-sm inline-flex items-center gap-1 ${snd ? "bg-canales-yellow text-black" : "bg-white text-neutral-500"}`}
      >
        {snd ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
    </div>
  );
}

function BackgroundShell({ children }) {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0d1a3f" }}>
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.25) 1.5px, transparent 1.5px)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="absolute -left-40 top-24 w-[720px] h-[100px] opacity-80 pointer-events-none"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }} />
      <div className="absolute -right-40 bottom-40 w-[520px] h-[70px] opacity-70 pointer-events-none"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }} />
      <div className="relative max-w-4xl mx-auto p-4 md:p-6">{children}</div>
    </div>
  );
}

/* ============================================================================
   Create / Landing (no code in URL yet)
============================================================================ */
function DueloCreate() {
  const [busy, setBusy] = useState(false);
  const [snd, setSnd] = useState(soundEnabled());
  const [joinCode, setJoinCode] = useState("");
  const [teamA, setTeamA] = useState("Tienda A");
  const [teamB, setTeamB] = useState("Tienda B");
  const [rounds, setRounds] = useState(3);
  const [strikes, setStrikes] = useState(3);
  const [enableSteal, setEnableSteal] = useState(true);
  const [mode, setMode] = useState("host"); // 'host' | 'auto'
  const nav = useNavigate();

  const create = async () => {
    setBusy(true);
    try {
      const hostId = stableClientId();
      const { data } = await api.post("/dijeron/battle/create", {
        host_id: hostId, mode,
        total_rounds: rounds, max_strikes: strikes,
        enable_steal: enableSteal,
        team_a_name: teamA.trim() || "Tienda A",
        team_b_name: teamB.trim() || "Tienda B",
      });
      if (!data.ok) throw new Error(data.detail || "No hay encuestas");
      nav(`/juego/canales-dijeron/duelo/${data.code}`, { replace: true });
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const join = () => {
    const code = (joinCode || "").trim().toUpperCase();
    if (code.length < 3) { toast.error("Código inválido"); return; }
    nav(`/juego/canales-dijeron/duelo/${code}`);
  };

  return (
    <BackgroundShell>
      <Header snd={snd} setSnd={setSnd} />
      <motion.div
        initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="text-center mb-6"
      >
        <h1 className="font-display font-black text-5xl md:text-6xl text-canales-yellow leading-none"
          style={{ WebkitTextStroke: "3px black", textShadow: "5px 5px 0px #E3000F", transform: "rotate(-1deg)" }}>
          DUELO POR
        </h1>
        <h1 className="font-display font-black text-5xl md:text-6xl text-white leading-none mt-1"
          style={{ WebkitTextStroke: "3px black", textShadow: "5px 5px 0px #E3000F", transform: "rotate(1deg)" }}>
          TIENDAS
        </h1>
        <p className="mt-3 text-white/80 font-body">
          Dos tiendas · turnos · 3 strikes · robo del bote · anfitrión en vivo
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Create */}
        <div className="brutal bg-white p-6 space-y-3" data-testid="duelo-create-card">
          <div className="flex items-center gap-2">
            <Crown className="w-6 h-6 text-canales-red" />
            <h2 className="font-display font-black text-2xl">Crear sala (anfitrión)</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue">Tienda A</span>
              <Input value={teamA} onChange={(e) => setTeamA(e.target.value)} data-testid="cfg-team-a" className="brutal-sm mt-1" />
            </label>
            <label className="block">
              <span className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">Tienda B</span>
              <Input value={teamB} onChange={(e) => setTeamB(e.target.value)} data-testid="cfg-team-b" className="brutal-sm mt-1" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-700">Rondas</span>
              <select
                value={rounds}
                onChange={(e) => setRounds(parseInt(e.target.value, 10))}
                data-testid="cfg-rounds"
                className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold"
              >
                {[1, 3, 5, 7].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-700">Strikes</span>
              <select
                value={strikes}
                onChange={(e) => setStrikes(parseInt(e.target.value, 10))}
                data-testid="cfg-strikes"
                className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold"
              >
                {[2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-700">Robo</span>
              <select
                value={enableSteal ? "1" : "0"}
                onChange={(e) => setEnableSteal(e.target.value === "1")}
                data-testid="cfg-steal"
                className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold"
              >
                <option value="1">Sí</option>
                <option value="0">No</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-700">Modo</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              data-testid="cfg-mode"
              className="brutal-sm mt-1 w-full py-2 px-2 bg-white font-display font-bold"
            >
              <option value="host">Anfitrión decide (tipo TV — aprueba cada respuesta)</option>
              <option value="auto">Automático (el sistema valida con fuzzy match)</option>
            </select>
          </label>
          <button
            onClick={create}
            disabled={busy}
            data-testid="duelo-create-btn"
            className="w-full brutal-btn bg-canales-red text-white px-6 py-4 font-display font-black text-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
            {busy ? "Creando…" : "Crear sala"}
          </button>
        </div>

        {/* Join */}
        <div className="brutal bg-white p-6 space-y-3" data-testid="duelo-join-card">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-canales-blue" />
            <h2 className="font-display font-black text-2xl">Unirme a sala</h2>
          </div>
          <p className="text-sm text-neutral-600 font-body">
            Pide el código de 4 letras al anfitrión.
          </p>
          <Input
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="CÓDIGO"
            data-testid="duelo-join-code"
            maxLength={6}
            className="brutal-sm text-center py-6 text-3xl font-display font-black tracking-widest"
          />
          <button
            onClick={join}
            data-testid="duelo-join-btn"
            className="w-full brutal-btn bg-canales-blue text-white px-6 py-4 font-display font-black text-lg inline-flex items-center justify-center gap-2"
          >
            <ChevronRight className="w-5 h-5" /> Entrar
          </button>
        </div>
      </div>
    </BackgroundShell>
  );
}

/* ============================================================================
   Room (has code — connect via WebSocket)
============================================================================ */
function TeamCard({ team, letter, active, isHost, onRename }) {
  const [editing, setEditing] = useState(false);
  const displayName = team?.name || `Tienda ${letter}`;
  const [name, setName] = useState(displayName);
  useEffect(() => setName(team?.name || `Tienda ${letter}`), [team?.name, letter]);
  // Inline style — `.brutal` global class sets background:#fff, so we bypass
  // Tailwind's cascade by using an inline style (highest specificity).
  const bg = letter === "A" ? "#1D4E89" : "#E3000F";

  return (
    <motion.div
      animate={active ? { scale: [1, 1.02, 1] } : { scale: 1 }}
      transition={{ repeat: active ? Infinity : 0, duration: 1.4 }}
      style={{ background: bg }}
      className={`brutal p-3 text-white ${active ? "ring-4 ring-canales-yellow" : ""}`}
      data-testid={`duelo-team-${letter}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-display font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
            <span>Equipo {letter}</span>
            {active && <span className="text-canales-yellow">· TURNO</span>}
          </div>
          {editing && isHost ? (
            <form
              onSubmit={(e) => { e.preventDefault(); onRename(name); setEditing(false); }}
              className="flex items-center gap-1 mt-1"
            >
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="brutal-sm bg-white text-black py-1 h-auto text-sm"
                autoFocus
              />
              <button type="submit" className="brutal-sm bg-canales-yellow text-black text-xs px-2 py-1 font-display font-black">OK</button>
            </form>
          ) : isHost ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="font-display font-black text-xl leading-tight truncate w-full text-left text-white hover:underline"
              title="Editar nombre"
              data-testid={`duelo-team-${letter}-name`}
            >
              {displayName}
            </button>
          ) : (
            <div
              className="font-display font-black text-xl leading-tight truncate text-white"
              data-testid={`duelo-team-${letter}-name`}
            >
              {displayName}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-display font-black uppercase opacity-70">pts</div>
          <div className="font-display font-black text-3xl leading-none text-canales-yellow" data-testid={`duelo-team-${letter}-score`}>
            {team?.score || 0}
          </div>
        </div>
      </div>
      <div className="mt-2 text-[11px] font-body opacity-90 truncate">
        {(team?.players || []).length === 0
          ? <span className="italic opacity-60">sin jugadores</span>
          : `${(team?.players || []).length} jugador(es)`}
      </div>
    </motion.div>
  );
}

function StrikeRow({ count, max }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <motion.div
          key={n}
          animate={n <= count ? { rotate: [0, -15, 15, 0] } : {}}
          style={n <= count ? { background: "#E3000F" } : undefined}
          className={`w-9 h-9 brutal-sm flex items-center justify-center ${
            n <= count ? "text-white" : "bg-white text-neutral-200"
          }`}
        >
          <XIcon className="w-5 h-5" strokeWidth={4} />
        </motion.div>
      ))}
    </div>
  );
}

function BoardSlot({ answer, lang, teamOfHitter }) {
  const revealed = answer.revealed_text;
  const label = revealed ? (lang === "en" && revealed.en ? revealed.en : revealed.es) : null;
  // Inline background beats the global `.brutal { background: #fff }`.
  const bgColor = revealed
    ? (teamOfHitter === "A" ? "#DBEAFE" : teamOfHitter === "B" ? "#FEE2E2" : "#FFF5E1")
    : "#1D4E89";
  return (
    <motion.div
      layout
      className={`brutal flex items-center justify-between px-4 py-3 ${revealed ? "text-black" : "text-white"}`}
      style={{ minHeight: "56px", background: bgColor }}
      data-testid={`duelo-slot-${answer.rank}`}
    >
      {revealed ? (
        <>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-display font-black text-2xl text-canales-red shrink-0">#{answer.rank}</span>
            <span className="font-display font-black text-base md:text-lg tracking-wide truncate">{label}</span>
          </div>
          <div className="brutal-sm bg-canales-red text-white px-3 py-1 font-display font-black text-xl shrink-0">
            {answer.points}
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-between">
          <span className="font-display font-black text-3xl text-canales-yellow">{answer.rank}</span>
          <span className="text-white/50 font-mono text-sm">{"·".repeat(Math.min(answer.text_len || 8, 20))}</span>
        </div>
      )}
    </motion.div>
  );
}

const QUICK_CHATS = ["¡Rápido! ⏱️", "Piensen bien 🤔", "¡Vamos equipo! 💪", "¡Ojo con señuelos!", "😂", "🔥"];

function ChatPanel({ chat, input, setInput, onSend }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat.length]);
  const teamColor = (t) => (t === "A" ? "#1D4E89" : t === "B" ? "#E3000F" : "#525252");
  return (
    <div className="brutal bg-white p-3 mt-3" data-testid="duelo-chat">
      <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue mb-2 flex items-center gap-1">
        <MessageCircle className="w-3 h-3" /> Chat en vivo
      </div>
      <div className="max-h-44 overflow-y-auto space-y-1 mb-2" data-testid="duelo-chat-messages">
        {chat.length === 0 ? (
          <div className="text-xs italic text-neutral-400 text-center py-3">
            Escribe para coordinar con tu equipo entre rondas…
          </div>
        ) : chat.map((m) => (
          <div key={m.id} className="text-sm font-body leading-snug" data-testid="duelo-chat-msg">
            <span
              className="font-display font-black text-[11px] px-1.5 py-0.5 text-white mr-1 inline-block align-middle"
              style={{ background: teamColor(m.team) }}
            >
              {m.role === "host" ? "★ " : ""}{m.name}{m.team ? ` · T${m.team}` : ""}
            </span>
            <span className="text-neutral-800 break-words">{m.text}</span>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {QUICK_CHATS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onSend(null, q)}
            data-testid="duelo-chat-quick"
            className="brutal-sm bg-canales-yellow text-black px-2 py-0.5 text-xs font-display font-bold hover:bg-yellow-300"
          >
            {q}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => onSend(e)} className="flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe un mensaje…"
          data-testid="duelo-chat-input"
          maxLength={240}
          className="brutal-sm flex-1 py-3 text-sm"
        />
        <button
          type="submit"
          data-testid="duelo-chat-send"
          className="brutal-btn bg-canales-blue text-white px-3 py-2 font-display font-black inline-flex items-center gap-1"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function DueloRoom() {
  const { code } = useParams();
  const { user } = useAuth() || {};
  const { lang } = useLang();
  const nav = useNavigate();

  const uidRef = useRef(user?.user_id || stableClientId());
  const wsRef = useRef(null);

  const [state, setState] = useState(null);
  const [connectStatus, setConnectStatus] = useState("connecting");
  const [name, setName] = useState(user?.name || "");
  const [joined, setJoined] = useState(false);
  const [teamChoice, setTeamChoice] = useState("");
  const [input, setInput] = useState("");
  const [snd, setSnd] = useState(soundEnabled());
  const [flash, setFlash] = useState(null);
  const [feed, setFeed] = useState([]);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const lastTickRef = useRef(0);

  // -------- WebSocket connection --------
  useEffect(() => {
    let ws = null;
    let alive = true;

    try {
      ws = new WebSocket(buildWsUrl(code));
    } catch (_) {
      setConnectStatus("error");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => alive && setConnectStatus("open");
    ws.onclose = () => alive && setConnectStatus("closed");
    ws.onerror = () => alive && setConnectStatus("error");
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "error") { toast.error(msg.message || "Error"); return; }
        if (msg.type === "chat") {
          setChat((c) => {
            if (c.some((m) => m.id === msg.id)) return c;
            return [...c, { id: msg.id, name: msg.name, team: msg.team, role: msg.role, text: msg.text }].slice(-60);
          });
          return;
        }
        if (msg.type === "hit" || msg.type === "steal_hit") {
          if (msg.type === "steal_hit") { stopSuspense(); playStealWin(); }
          else playReveal();
          setFlash("hit"); setTimeout(() => setFlash(null), 400);
          setFeed((f) => [{
            id: Date.now(), type: "hit",
            user_name: nameOf(msg.user_id, msg), team: msg.team,
            rank: msg.rank,
            text: lang === "en" && msg.text_en ? msg.text_en : msg.text,
            points: msg.points,
          }, ...f].slice(0, 12));
        } else if (msg.type === "strike" || msg.type === "steal_fail") {
          if (msg.type === "steal_fail") { stopSuspense(); playStealFail(); }
          else playMiss();
          setFlash("miss"); setTimeout(() => setFlash(null), 300);
          setFeed((f) => [{
            id: Date.now(), type: msg.type,
            user_name: nameOf(msg.user_id, msg), team: msg.team,
            guess: msg.guess, decoy: msg.decoy,
          }, ...f].slice(0, 12));
        } else if (msg.type === "steal_open") {
          playSteal();
        } else if (msg.type === "host_reveal") {
          playReveal();
        } else if (msg.type === "round_end") {
          stopSuspense(); playApplause();
        } else if (msg.type === "game_end") {
          stopSuspense(); playApplause();
        }
        if (msg.status) setState(msg);
      } catch (_) {}
    };
    return () => {
      alive = false;
      if (ws) try { ws.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const nameOf = (uid, fallback) => {
    // Try to resolve a friendlier name for feed entries.
    const p = (state?.players || []).find((pp) => pp.user_id === uid);
    return p?.name || fallback?.user_name || "Alguien";
  };

  // Countdown tick sound in the last 10s
  useEffect(() => {
    if (!state || (state.status !== "playing" && state.status !== "steal")) return;
    if (state.time_remaining > 0 && state.time_remaining <= 10 && state.time_remaining !== lastTickRef.current) {
      lastTickRef.current = state.time_remaining;
      playCountdown();
    }
  }, [state]);

  // Suspense music bed — plays during the steal window and while the host is
  // deciding on a pending guess (host mode). Stops otherwise / when muted.
  useEffect(() => {
    const inSteal = state?.status === "steal";
    const pending = state?.mode === "host" && !!state?.pending_guess && state?.status === "playing";
    if (snd && (inSteal || pending)) startSuspense();
    else stopSuspense();
  }, [state?.status, state?.pending_guess, state?.mode, snd]);

  // Kill any running suspense loop on unmount.
  useEffect(() => () => stopSuspense(), []);

  // Seed / merge chat history from server state (covers late joiners & reconnects).
  useEffect(() => {
    const log = state?.chat_log;
    if (!log || log.length === 0) return;
    setChat((c) => {
      const ids = new Set(c.map((m) => m.id));
      const add = log
        .filter((m) => !ids.has(m.id))
        .map((m) => ({ id: m.id, name: m.name, team: m.team, role: m.role, text: m.text }));
      return add.length ? [...c, ...add].slice(-60) : c;
    });
  }, [state?.chat_log]);

  const send = (msg) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify(msg));
  };

  const isHost = state && state.host_id === uidRef.current;
  const myPlayer = (state?.players || []).find((p) => p.user_id === uidRef.current);
  const myTeam = myPlayer?.team || "";
  const canGuess = joined
    && !isHost
    && myTeam
    && myTeam === state?.active_team
    && (state?.status === "playing" || state?.status === "steal");

  const doJoin = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error("Escribe tu nombre"); return; }
    // Host role auto-detected server-side by matching host_id.
    send({ type: "join", user_id: uidRef.current, name: name.trim(), team: teamChoice });
    setJoined(true);
  };

  const chooseTeam = (t) => {
    setTeamChoice(t);
    send({ type: "join_team", team: t });
  };

  const doStart = () => { playStart(); send({ type: "start" }); };
  const doNext = () => send({ type: "next_round" });
  const doGuess = (e) => {
    e?.preventDefault?.();
    if (!input.trim()) return;
    send({ type: "guess", text: input });
    setInput("");
  };

  const sendChat = (e, preset) => {
    e?.preventDefault?.();
    const text = (preset ?? chatInput).trim();
    if (!text) return;
    send({ type: "chat", text });
    if (preset == null) setChatInput("");
  };

  // Host controls
  const hostApprove = () => send({ type: "host_approve" });
  const hostReject = (kind = "strike") => send({ type: "host_reject", kind });
  const hostReveal = (rank) => send({ type: "host_reveal", rank });
  const hostStrike = () => send({ type: "host_strike" });
  const hostSwap = () => send({ type: "host_swap_turn" });
  const hostEndRound = (winner) => send({ type: "host_end_round", winner_team: winner });
  const hostConfig = (patch) => send({ type: "config", ...patch });
  const hostRename = (team, newName) => send({ type: "rename_team", team, name: newName });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado — mándalo por WhatsApp");
    } catch { toast.error("No pude copiar"); }
  };

  // -------- Render states --------
  if (connectStatus === "connecting") {
    return (
      <BackgroundShell>
        <Header snd={snd} setSnd={setSnd} />
        <div className="text-center text-white/70 py-16">
          <Loader2 className="w-8 h-8 animate-spin inline-block" />
          <div className="mt-2 text-sm">Conectando a la sala…</div>
        </div>
      </BackgroundShell>
    );
  }
  if (connectStatus === "error" || connectStatus === "closed") {
    return (
      <BackgroundShell>
        <Header snd={snd} setSnd={setSnd} />
        <div className="brutal bg-white p-8 text-center">
          <div className="font-display font-black text-2xl mb-2">Sala perdida</div>
          <p className="text-sm text-neutral-600 mb-4">La conexión se cerró o la sala no existe.</p>
          <button
            onClick={() => nav("/juego/canales-dijeron/duelo")}
            className="brutal-btn bg-canales-red text-white px-4 py-2 inline-flex items-center gap-1"
          >
            <Home className="w-4 h-4" /> Volver
          </button>
        </div>
      </BackgroundShell>
    );
  }

  // Join form (name + team choice)
  if (!joined) {
    return (
      <BackgroundShell>
        <Header snd={snd} setSnd={setSnd} />
        <div className="brutal bg-white p-6 max-w-md mx-auto space-y-4" data-testid="duelo-join-form">
          <div className="flex items-center gap-2">
            <Swords className="w-6 h-6 text-canales-red" />
            <div className="font-display font-black text-2xl">Sala {code}</div>
          </div>
          <p className="text-sm font-body text-neutral-600">
            Escribe tu nombre y elige tu tienda. Si tú creaste la sala, entras como <b>anfitrión</b> automáticamente.
          </p>
          <form onSubmit={doJoin} className="space-y-3">
            <label className="block">
              <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">Tu nombre</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="duelo-name" className="brutal-sm mt-1" autoFocus />
            </label>
            <div>
              <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">Elige tienda</span>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["A", "B"].map((t) => {
                  const label = state?.teams?.[t]?.name || `Tienda ${t}`;
                  const on = teamChoice === t;
                  const bg = t === "A" ? "bg-canales-blue" : "bg-canales-red";
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setTeamChoice(t)}
                      data-testid={`duelo-pick-team-${t}`}
                      className={`brutal-btn ${on ? bg + " text-white" : "bg-white text-black"} px-3 py-3 font-display font-black inline-flex items-center justify-center gap-1`}
                    >
                      {t === "A" ? "🅰️" : "🅱️"} {label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">
                También puedes entrar sin equipo (como <b>observador / anfitrión</b>).
              </div>
            </div>
            <button
              type="submit"
              data-testid="duelo-join-submit"
              className="w-full brutal-btn bg-canales-blue text-white px-4 py-3 font-display font-black inline-flex items-center justify-center gap-1"
            >
              <ChevronRight className="w-5 h-5" /> Entrar
            </button>
          </form>
        </div>
      </BackgroundShell>
    );
  }

  // Connected + joined — main room UI
  const status = state?.status || "waiting";
  const teams = state?.teams || {};
  const survey = state?.survey;

  return (
    <BackgroundShell>
      <Header snd={snd} setSnd={setSnd} />

      {/* Code + share */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="brutal-sm bg-canales-yellow text-black px-3 py-1 font-display font-black tracking-widest inline-flex items-center gap-2">
          Sala <span className="text-canales-red text-xl">{code}</span>
          {isHost && (
            <span className="ml-1 brutal-sm bg-canales-red text-white text-[10px] px-2 py-0.5 inline-flex items-center gap-1">
              <Crown className="w-3 h-3" /> ANFITRIÓN
            </span>
          )}
          {!isHost && myTeam && (
            <span className={`ml-1 brutal-sm text-white text-[10px] px-2 py-0.5 ${myTeam === "A" ? "bg-canales-blue" : "bg-canales-red"}`}>
              Tienda {myTeam}
            </span>
          )}
        </div>
        <button onClick={copyLink} data-testid="duelo-copy-link" className="brutal-sm bg-white px-3 py-1 text-xs inline-flex items-center gap-1">
          <Copy className="w-3 h-3" /> Copiar link
        </button>
      </div>

      {/* Team scoreboard */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <TeamCard team={teams.A} letter="A" active={state?.active_team === "A" && (status === "playing" || status === "steal")} isHost={isHost} onRename={(n) => hostRename("A", n)} />
        <TeamCard team={teams.B} letter="B" active={state?.active_team === "B" && (status === "playing" || status === "steal")} isHost={isHost} onRename={(n) => hostRename("B", n)} />
      </div>

      {/* Round summary bar */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="brutal-sm bg-white p-2 text-center">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500">Ronda</div>
          <div className="font-display font-black text-xl">
            {Math.min((state?.round_idx || 0) + 1, state?.total_rounds || 1)}/{state?.total_rounds || 1}
          </div>
        </div>
        <div className="brutal-sm bg-white p-2 text-center">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500">Bote</div>
          <div className="font-display font-black text-xl text-canales-red" data-testid="duelo-pot">
            {state?.round_pot || 0}
          </div>
        </div>
        <div className="brutal-sm bg-white p-2 text-center">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500">Strikes</div>
          <div className="mt-1"><StrikeRow count={state?.strikes || 0} max={state?.max_strikes || 3} /></div>
        </div>
        <motion.div
          animate={state?.time_remaining <= 10 && (status === "playing" || status === "steal") ? { scale: [1, 1.06, 1] } : {}}
          transition={{ repeat: Infinity, duration: 0.6 }}
          style={{ background: state?.time_remaining <= 10 && (status === "playing" || status === "steal") ? "#E3000F" : "#1D4E89" }}
          className="brutal-sm p-2 text-center text-white"
        >
          <div className="text-[10px] font-display font-black uppercase tracking-widest opacity-90">Tiempo</div>
          <div className="font-display font-black text-xl">
            00:{String(state?.time_remaining || 0).padStart(2, "0")}
          </div>
        </motion.div>
      </div>

      {/* WAITING */}
      {status === "waiting" && (
        <div className="brutal bg-white p-6 space-y-3">
          <div className="font-display font-black text-2xl">Sala lista — comparte el código</div>
          <div className="brutal-sm bg-canales-yellow text-black px-6 py-3 inline-block font-display font-black text-4xl tracking-widest">
            {code}
          </div>
          <p className="text-sm font-body text-neutral-600">
            Cada tienda necesita al menos 1 jugador. {isHost ? "Cuando estén todos, toca Empezar." : "Espera al anfitrión."}
          </p>
          {isHost && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-neutral-200">
              <div className="text-xs text-neutral-700 space-y-2">
                <div className="font-display font-black uppercase tracking-widest text-canales-red flex items-center gap-1">
                  <Settings className="w-4 h-4" /> Configuración
                </div>
                <label className="block">
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest">Modo</span>
                  <select
                    value={state?.mode || "host"}
                    onChange={(e) => hostConfig({ mode: e.target.value })}
                    className="brutal-sm w-full py-1 px-2 mt-0.5 bg-white font-display font-bold text-sm"
                  >
                    <option value="host">Anfitrión decide (aprueba)</option>
                    <option value="auto">Automático (fuzzy)</option>
                  </select>
                </label>
                <div className="grid grid-cols-3 gap-1">
                  <label>
                    <span className="text-[10px] font-display font-bold uppercase tracking-widest">Rondas</span>
                    <select
                      value={state?.total_rounds || 3}
                      onChange={(e) => hostConfig({ total_rounds: parseInt(e.target.value, 10) })}
                      className="brutal-sm w-full py-1 px-2 mt-0.5 bg-white font-display font-bold text-sm"
                    >
                      {[1, 3, 5, 7].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="text-[10px] font-display font-bold uppercase tracking-widest">Strikes</span>
                    <select
                      value={state?.max_strikes || 3}
                      onChange={(e) => hostConfig({ max_strikes: parseInt(e.target.value, 10) })}
                      className="brutal-sm w-full py-1 px-2 mt-0.5 bg-white font-display font-bold text-sm"
                    >
                      {[2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="text-[10px] font-display font-bold uppercase tracking-widest">Robo</span>
                    <select
                      value={state?.enable_steal ? "1" : "0"}
                      onChange={(e) => hostConfig({ enable_steal: e.target.value === "1" })}
                      className="brutal-sm w-full py-1 px-2 mt-0.5 bg-white font-display font-bold text-sm"
                    >
                      <option value="1">Sí</option>
                      <option value="0">No</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="flex flex-col justify-end">
                <button
                  onClick={doStart}
                  disabled={((teams.A?.players || []).length === 0) || ((teams.B?.players || []).length === 0)}
                  data-testid="duelo-start"
                  className="brutal-btn bg-canales-red text-white px-6 py-4 font-display font-black inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Play className="w-5 h-5" /> Empezar duelo
                </button>
              </div>
            </div>
          )}
          {!isHost && !myTeam && (
            <div className="pt-2 border-t border-neutral-200">
              <div className="text-xs font-display font-black uppercase tracking-widest mb-1 text-canales-red">Elige tu tienda</div>
              <div className="grid grid-cols-2 gap-2">
                {["A", "B"].map((t) => (
                  <button
                    key={t}
                    onClick={() => chooseTeam(t)}
                    data-testid={`duelo-choose-team-${t}`}
                    className={`brutal-btn ${t === "A" ? "bg-canales-blue" : "bg-canales-red"} text-white px-3 py-2 font-display font-black inline-flex items-center justify-center gap-1`}
                  >
                    {t === "A" ? "🅰️" : "🅱️"} {teams[t]?.name || `Tienda ${t}`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PLAYING / STEAL / ROUND_END */}
      {(status === "playing" || status === "steal" || status === "round_end") && survey && (
        <>
          {status === "steal" && (
            <motion.div
              initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              style={{ background: "#FFD100" }}
              className="brutal text-black p-3 mb-3 flex items-center gap-2"
              data-testid="duelo-steal-banner"
            >
              <ShieldAlert className="w-6 h-6 text-canales-red" />
              <div className="font-display font-black text-lg">
                ¡ROBO! {teams[state.active_team]?.name || `Tienda ${state.active_team}`} tiene UNA oportunidad para llevarse el bote de {state.round_pot} pts.
              </div>
            </motion.div>
          )}

          {/* Question */}
          <div className="brutal p-4 mb-4" style={{ background: "#FFF5E1" }}>
            <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red mb-1">
              Ronda {(state.round_idx || 0) + 1} · Turno: {teams[state.active_team]?.name || `Tienda ${state.active_team}`}
            </div>
            <div className="font-display font-black text-xl md:text-2xl leading-tight">
              "{lang === "en" && survey.question_en ? survey.question_en : survey.question_es}"
            </div>
            {(survey.decoy_count || 0) > 0 && (
              <div className="mt-1 text-[11px] font-body text-orange-600 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" /> ¡Cuidado! {survey.decoy_count} señuelo(s) oculto(s) causan strike si los dicen.
              </div>
            )}
          </div>

          {/* Board */}
          <div className="grid grid-cols-1 gap-2 mb-3">
            {survey.answers.map((a) => (
              <BoardSlot key={a.rank} answer={a} lang={lang} teamOfHitter={a.hit_by_team} />
            ))}
          </div>

          {/* Guess input (for active-team players) */}
          {status !== "round_end" && (
            <div className="brutal bg-white p-3 mb-3">
              {canGuess ? (
                <form onSubmit={doGuess} className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-canales-red shrink-0" />
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={status === "steal" ? "🎯 Respuesta para robar…" : "Tu respuesta…"}
                    data-testid="duelo-input"
                    autoFocus
                    className="brutal-sm flex-1 py-4 text-base"
                  />
                  <button
                    type="submit"
                    data-testid="duelo-submit"
                    className={`brutal-btn px-4 py-3 font-display font-black inline-flex items-center gap-1 ${flash === "hit" ? "bg-green-600 text-white" : flash === "miss" ? "bg-canales-red text-white" : "bg-canales-blue text-white"}`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </form>
              ) : (
                <div className="text-sm font-body text-neutral-500 italic text-center py-2" data-testid="duelo-waiting">
                  {isHost
                    ? "Modo anfitrión: espera la respuesta del equipo activo y decide abajo."
                    : (!myTeam
                        ? "Estás como observador. Espera a que termine el duelo."
                        : (myTeam === state.active_team
                            ? "Espera turno / aprobación del anfitrión."
                            : `Turno de ${teams[state.active_team]?.name || `Tienda ${state.active_team}`}…`))}
                </div>
              )}
            </div>
          )}

          {/* Host controls */}
          {isHost && (status === "playing" || status === "steal") && (
            <div className="brutal bg-white p-3 mb-3" data-testid="duelo-host-panel">
              <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red mb-2 flex items-center gap-1">
                <Crown className="w-3 h-3" /> Panel del anfitrión
              </div>
              {/* Pending guess (host mode) */}
              {state.mode === "host" && state.pending_guess ? (
                <div className="brutal-sm p-3 mb-2" style={{ background: "#FFD100" }}>
                  <div className="text-[10px] font-display font-black uppercase tracking-widest">
                    Respuesta pendiente · {state.pending_guess.team ? `Tienda ${state.pending_guess.team}` : ""}
                  </div>
                  <div className="font-display font-black text-2xl leading-tight" data-testid="duelo-pending">
                    "{state.pending_guess.guess}"
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button onClick={hostApprove} data-testid="duelo-host-approve" className="brutal-sm bg-green-600 text-white px-3 py-1 font-display font-black inline-flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Acierto
                    </button>
                    <button onClick={() => hostReject("strike")} data-testid="duelo-host-strike" className="brutal-sm bg-canales-red text-white px-3 py-1 font-display font-black inline-flex items-center gap-1">
                      <XCircle className="w-4 h-4" /> Strike
                    </button>
                    <button onClick={() => hostReject("ignore")} className="brutal-sm bg-white px-3 py-1 font-display font-bold inline-flex items-center gap-1">
                      <EyeOff className="w-4 h-4" /> Ignorar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-xs italic text-neutral-500 mb-2">
                  {state.mode === "host" ? "Esperando respuesta del equipo activo…" : "Modo automático — el sistema valida solo."}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button onClick={hostStrike} className="brutal-sm bg-canales-red text-white px-3 py-1 text-xs font-display font-bold inline-flex items-center gap-1">
                  <XIcon className="w-3 h-3" /> Dar strike
                </button>
                <button onClick={hostSwap} className="brutal-sm bg-canales-blue text-white px-3 py-1 text-xs font-display font-bold inline-flex items-center gap-1">
                  <Repeat className="w-3 h-3" /> Cambiar turno
                </button>
                <button onClick={() => hostEndRound()} className="brutal-sm bg-neutral-800 text-white px-3 py-1 text-xs font-display font-bold inline-flex items-center gap-1">
                  <StopCircle className="w-3 h-3" /> Terminar ronda
                </button>
                <div className="w-full mt-1">
                  <div className="text-[10px] font-display font-bold uppercase tracking-widest text-neutral-500 mb-1">Revelar slot manualmente</div>
                  <div className="flex flex-wrap gap-1">
                    {survey.answers.filter((a) => !a.revealed_text).map((a) => (
                      <button
                        key={a.rank}
                        onClick={() => hostReveal(a.rank)}
                        className="brutal-sm bg-canales-yellow text-black px-2 py-0.5 text-xs font-display font-black"
                        title="Revelar sin dar créditos"
                      >
                        #{a.rank}
                      </button>
                    ))}
                    {survey.answers.every((a) => a.revealed_text) && (
                      <span className="text-[11px] italic text-neutral-500">Todas reveladas</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Round end */}
          {status === "round_end" && (
            <div className="brutal bg-white p-4 mb-3" data-testid="duelo-round-end">
              <div className="font-display font-black text-2xl">
                Fin de la ronda {(state.round_idx || 0) + 1}
              </div>
              <div className="text-sm font-body text-neutral-700 mt-1">
                Bote {state.last_event?.pot ?? state.round_pot} pts →{" "}
                <b>{teams[state.last_event?.winner_team]?.name || `Tienda ${state.last_event?.winner_team}`}</b>{" "}
                <span className="text-xs italic text-neutral-500">({state.last_event?.reason})</span>
              </div>
              {isHost && (
                <button
                  onClick={doNext}
                  data-testid="duelo-next"
                  className="brutal-btn bg-canales-red text-white px-4 py-2 mt-3 inline-flex items-center gap-1 font-display font-black"
                >
                  <ChevronsRight className="w-4 h-4" />
                  {(state.round_idx || 0) + 1 >= state.total_rounds ? "Ver ganador" : "Siguiente ronda"}
                </button>
              )}
            </div>
          )}

          {/* Live feed */}
          {feed.length > 0 && (
            <div className="brutal bg-white p-3 max-h-56 overflow-y-auto" data-testid="duelo-feed">
              <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-500 mb-1">
                En vivo
              </div>
              <ul className="text-sm font-body space-y-1">
                <AnimatePresence>
                  {feed.map((f) => (
                    <motion.li
                      key={f.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={f.type === "hit" ? "text-green-700" : "text-canales-red"}
                    >
                      {f.type === "hit"
                        ? <>✓ <b>{f.user_name}</b> {f.team ? `[T${f.team}]` : ""} acertó #{f.rank} · "{f.text}" (+{f.points})</>
                        : <>✗ <b>{f.user_name}</b> {f.team ? `[T${f.team}]` : ""} falló "{f.guess}" {f.decoy ? <span className="italic">(¡señuelo: {f.decoy}!)</span> : null}</>}
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            </div>
          )}
        </>
      )}

      {/* DONE */}
      {status === "done" && (
        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
          className="brutal bg-white p-6 text-center space-y-3 mt-4" data-testid="duelo-done">
          <Trophy className="w-14 h-14 text-canales-red mx-auto" />
          <div className="font-display font-black text-4xl">¡Duelo terminado!</div>
          {(() => {
            const a = teams.A?.score || 0; const b = teams.B?.score || 0;
            const winner = a === b ? null : (a > b ? "A" : "B");
            return (
              <div>
                {winner
                  ? <>
                      <div className="text-sm font-display font-bold uppercase tracking-widest text-neutral-500">Gana</div>
                      <div className="font-display font-black text-3xl">
                        {teams[winner]?.name || `Tienda ${winner}`}
                      </div>
                      <div className="text-canales-red font-display font-black text-5xl mt-1">
                        {teams[winner]?.score || 0} pts
                      </div>
                    </>
                  : <div className="font-display font-black text-3xl text-canales-red">¡Empate!</div>}
              </div>
            );
          })()}
          <div className="flex gap-2 justify-center pt-2">
            <button onClick={() => nav("/juego/canales-dijeron/duelo", { replace: true })} className="brutal-btn bg-canales-blue text-white px-4 py-2 inline-flex items-center gap-1">
              <Swords className="w-4 h-4" /> Nuevo duelo
            </button>
            <Link to="/juego/canales-dijeron" className="brutal-btn bg-white px-4 py-2 inline-flex items-center gap-1">
              <Home className="w-4 h-4" /> Salir
            </Link>
          </div>
        </motion.div>
      )}

      {/* Live chat — available to everyone, anytime (coordinate between rounds) */}
      <ChatPanel chat={chat} input={chatInput} setInput={setChatInput} onSend={sendChat} />
    </BackgroundShell>
  );
}

export default function CanalesDijeronDuelo() {
  const { code } = useParams();
  return code ? <DueloRoom /> : <DueloCreate />;
}
